import React from 'react';
import { IconX } from '@tabler/icons-react';
import MNavigationButton from '@/components/ui/atoms/MNavigationButton';
import MLoader from '@/components/ui/atoms/MLoader';
import { MemberStats } from '@/types/familyStats';
import { HealthProfileSummary, MedicalDetailType, MedicalDetailStatus } from '@/types/health-profile';
import { isChild as isChildUtil } from '@/types/member';
import styles from './ProfileTrackerCard.module.css';

export interface ProfileTrackerCardProps {
  isOpen: boolean;
  onClose: () => void;
  profileScore: number;
  profileStrength: 'low' | 'moderate' | 'strong';
  memberStats?: MemberStats | null;
  healthProfileSummary?: HealthProfileSummary | null;
  onNavigate?: (path: string) => void;
  onUploadDocument?: () => void; // Callback to open document upload options
  hasEmergencyContact?: boolean; // Whether emergency contact is filled
  memberType?: 'h' | 'p'; // 'h' for human, 'p' for pet
  memberDob?: {
    dob_year?: number;
    dob_month?: number;
    dob_day?: number;
  };
  documentTypeCounts?: {
    prescription?: number;
    labReport?: number;
    dischargeSummary?: number;
    imagingReport?: number;
    insuranceCard?: number;
    medicalBill?: number;
  };
  documentYearSpan?: number; // Number of years covered by documents
  loading?: boolean; // Show loader while data is being fetched
}

const ProfileTrackerCard: React.FC<ProfileTrackerCardProps> = ({
  isOpen,
  onClose,
  profileScore,
  profileStrength,
  memberStats,
  healthProfileSummary,
  onNavigate,
  onUploadDocument,
  hasEmergencyContact = false,
  memberType = 'h',
  memberDob,
  documentTypeCounts,
  documentYearSpan,
  loading = false,
}) => {
  if (!isOpen) return null;

  // Show loader while data is being fetched
  if (loading) {
    return (
      <div className={styles.overlay}>
        <div className={styles.card}>
          <MLoader />
        </div>
      </div>
    );
  }

  // Helper to check if a health profile field is missing or not answered
  const isHealthFieldMissing = (type: MedicalDetailType): boolean => {
    if (!healthProfileSummary?.summary) return true;
    const detail = healthProfileSummary.summary[type];
    return !detail || detail.status === MedicalDetailStatus.NOT_ANSWERED;
  };

  // Generate dynamic actions for LOW profile strength
  const getLowActions = () => {
    const actions: Array<{ text: string; description?: string; path: string }> = [];
    const isPet = memberType === 'p';
    const isChild = isChildUtil(memberDob);
    const isAdult = !isPet && !isChild;

    // Check for missing critical health profile items
    if (isHealthFieldMissing(MedicalDetailType.BLOOD_TYPE)) {
      actions.push({ text: 'Add blood group', path: '/profile/health-profile/blood-group' });
    }
    if (isHealthFieldMissing(MedicalDetailType.ALLERGIES)) {
      actions.push({ text: 'Add allergies', path: '/profile/health-profile/allergies' });
    }
    if (isHealthFieldMissing(MedicalDetailType.CONDITIONS)) {
      actions.push({ text: 'Add chronic conditions', path: '/profile/health-profile/conditions/form' });
    }
    if (isHealthFieldMissing(MedicalDetailType.MEDICINE)) {
      actions.push({ text: 'Add current medicines', path: '/profile/health-profile/medication' });
    }
    
    // Surgeries and device implants - only for adults and children, not pets
    if (isHealthFieldMissing(MedicalDetailType.SURGERIES)) {
        actions.push({ text: 'Add surgeries', path: '/profile/health-profile/surgeries/SurgeriesForm' });
    }
    if (isHealthFieldMissing(MedicalDetailType.DEVICE_IMPLANTS)) {
        actions.push({ text: 'Add device implants', path: '/profile/health-profile/implant-devices/form' });
    }
    
    // Birth complications - only for children who are humans (not pets)
    if (isChild && !isPet && isHealthFieldMissing(MedicalDetailType.BIRTH_COMPLICATIONS)) {
      actions.push({ text: 'Add birth information', path: '/profile/health-profile/birth-complications' });
    }
    
    // Vaccination records - for children and pets, not adults
    if ((isChild || isPet) && isHealthFieldMissing(MedicalDetailType.VACCINE_TRACKER)) {
      actions.push({ text: 'Add vaccination records', path: '/profile/health-profile/vaccination-tracker' });
    }

    // Check for vitals
    if (!memberStats?.vitalsAdded) {
      actions.push({ text: 'Add vitals', path: '/profile/basic-information/vitals' });
    }

    // Check for insurance
    if (!memberStats?.insuranceAdded) {
      actions.push({ text: 'Add insurance', path: '/profile/basic-information/insurance' });
    }

    // Check for emergency contact
    if (!hasEmergencyContact) {
      actions.push({ text: 'Add emergency contacts', path: '/profile/basic-information/contact' });
    }

    // Check if no documents uploaded
    if (memberStats && memberStats.docsCount === 0) {
      actions.push({
        text: 'Upload first health document',
        description: 'Prescription or report',
        path: 'upload-document',
      });
    }

    return actions;
  };

  // Generate dynamic actions for MODERATE profile strength
  const getModerateActions = () => {
    const actions: Array<{ text: string; description?: string; path: string }> = [];
    const isPet = memberType === 'p';
    const isChild = isChildUtil(memberDob);
    const isAdult = !isPet && !isChild;

    // ADULT requirements for STRONG status
    if (isAdult) {
      // Check blood group and allergies (hard gates)
      if (isHealthFieldMissing(MedicalDetailType.BLOOD_TYPE)) {
        actions.push({
          text: 'Add blood group',
          description: 'Required for emergency-ready profile',
          path: '/profile/health-profile/blood-group',
        });
      }
      if (isHealthFieldMissing(MedicalDetailType.ALLERGIES)) {
        actions.push({
          text: 'Add allergies',
          description: 'Required for emergency-ready profile',
          path: '/profile/health-profile/allergies',
        });
      }

      // Check vitals
      if (!memberStats?.vitalsAdded) {
        actions.push({
          text: 'Add vitals',
          description: 'Height, weight, and other vital measurements',
          path: '/profile/basic-information/vitals',
        });
      }

      // Check document count (need 10+)
      if (memberStats && memberStats.docsCount < 10) {
        const remaining = 10 - memberStats.docsCount;
        actions.push({
          text: `Upload ${remaining} more document${remaining > 1 ? 's' : ''}`,
          description: 'Need 10+ documents for emergency-ready profile',
          path: 'upload-document',
        });
      }

      // Check for prescription AND lab reports
      const hasPrescription = (documentTypeCounts?.prescription || 0) > 0;
      const hasLabReport = (documentTypeCounts?.labReport || 0) > 0;
      if (!hasPrescription || !hasLabReport) {
        if (!hasPrescription && !hasLabReport) {
          actions.push({
            text: 'Upload prescription and lab report',
            description: 'Both required for emergency-ready profile',
            path: 'upload-document',
          });
        } else if (!hasPrescription) {
          actions.push({
            text: 'Upload at least 1 prescription',
            description: 'Required for emergency-ready profile',
            path: 'upload-document',
          });
        } else if (!hasLabReport) {
          actions.push({
            text: 'Upload at least 1 lab report',
            description: 'Required for emergency-ready profile',
            path: 'upload-document',
          });
        }
      }

      // Check longitudinal coverage (need 5+ years)
      const yearSpan = documentYearSpan || 0; // Treat undefined/null as 0
      if (yearSpan < 5) {
        const remaining = 5 - yearSpan;
        actions.push({
          text: `Add ${remaining} more year${remaining > 1 ? 's' : ''} of documents`,
          description: 'Need 5+ year health history.',
          path: 'upload-document',
        });
      }
    }

    // CHILD requirements for STRONG status
    if (isChild && !isPet) {
      // Check blood group, birth info, and vaccines (hard gates)
      if (isHealthFieldMissing(MedicalDetailType.BLOOD_TYPE)) {
        actions.push({
          text: 'Add blood group',
          description: 'Required for emergency-ready profile',
          path: '/profile/health-profile/blood-group',
        });
      }
      if (isHealthFieldMissing(MedicalDetailType.BIRTH_COMPLICATIONS)) {
        actions.push({
          text: 'Add birth information',
          description: 'Required for emergency-ready profile',
          path: '/profile/health-profile/birth-complications',
        });
      }
      if (isHealthFieldMissing(MedicalDetailType.VACCINE_TRACKER)) {
        actions.push({
          text: 'Add vaccination records',
          description: 'Required for emergency-ready profile',
          path: '/profile/health-profile/vaccination-tracker',
        });
      }

      // Suggest completing remaining sections if score is low
      if (profileScore < 75 && actions.length === 0) {
        actions.push({
          text: 'Complete health profile',
          description: 'Add more health information to strengthen profile',
          path: '/profile/health-profile',
        });
      }
    }

    // PET requirements for STRONG status
    if (isPet) {
      // Check vaccines and flea/tick (hard gates)
      if (isHealthFieldMissing(MedicalDetailType.VACCINE_TRACKER)) {
        actions.push({
          text: 'Add vaccination records',
          description: 'Required for emergency-ready profile',
          path: '/profile/health-profile/vaccination-tracker',
        });
      }
      if (isHealthFieldMissing(MedicalDetailType.PET_CARE)) {
        actions.push({
          text: 'Add flea/tick prevention details',
          description: 'Required for emergency-ready profile',
          path: '/profile/health-profile/pet-care',
        });
      }

      // Suggest completing remaining sections if score is low
      if (profileScore < 75 && actions.length === 0) {
        actions.push({
          text: 'Complete health profile',
          description: 'Add more health information to strengthen profile',
          path: '/profile/health-profile',
        });
      }
    }

    // General suggestions for adults (not gates, just helpful)
    if (isAdult) {
      if (memberStats && memberStats.memoriesCount === 0) {
        actions.push({
          text: 'Add at least 1 health memory',
          description: 'Helps track important health events',
          path: '/documents/health-memory',
        });
      }
      if (isHealthFieldMissing(MedicalDetailType.SURGERIES)) {
        actions.push({ text: 'Add surgeries (if any)', path: '/profile/health-profile/surgeries/SurgeriesForm' });
      }
      if (isHealthFieldMissing(MedicalDetailType.DEVICE_IMPLANTS)) {
        actions.push({ text: 'Add device implants (if any)', path: '/profile/health-profile/implant-devices/form' });
      }
      if (isHealthFieldMissing(MedicalDetailType.NOTE)) {
        actions.push({ text: 'Add Emergency notes', path: '/profile/health-profile/notes' });
      }
    }

    return actions;
  };

  // Determine state based on profile strength
  const getStateConfig = () => {
    switch (profileStrength) {
      case 'low':
        return {
          title: 'Needs attention',
          description: 'Some essential health details are missing. Add them to be ready for emergencies.',
          filledBars: 1,
          actions: getLowActions(),
        };
      case 'moderate':
        return {
          title: 'On track',
          description: 'You are on your way. A few more details will make this profile care-ready.',
          filledBars: 2,
          actions: getModerateActions(),
        };
      case 'strong':
        return {
          title: 'Prepared',
          description: 'You are emergency-ready. Keep building health continuity over time.',
          filledBars: 3,
          actions: [],
        };
      default:
        return {
          title: 'Needs attention',
          description: 'Some essential health details are missing. Add them to be ready for emergencies.',
          filledBars: 1,
          actions: getLowActions(),
        };
    }
  };

  const config = getStateConfig();

  const handleActionClick = (path: string) => {
    // Handle document upload action
    if (path === 'upload-document') {
      if (onUploadDocument) {
        onUploadDocument();
      }
      onClose();
      return;
    }

    // Blood group goes directly to the page without modal
    if (path === '/profile/health-profile/blood-group') {
      if (onNavigate) {
        onNavigate(path);
      }
      onClose();
      return;
    }

    // Map health profile paths to MedicalDetailType enum values
    const pathToTypeMap: Record<string, string> = {
      '/profile/health-profile/allergies': MedicalDetailType.ALLERGIES,
      '/profile/health-profile/conditions/form': MedicalDetailType.CONDITIONS,
      '/profile/health-profile/medication': MedicalDetailType.MEDICINE,
      '/profile/health-profile/surgeries/SurgeriesForm': MedicalDetailType.SURGERIES,
      '/profile/health-profile/implant-devices/form': MedicalDetailType.DEVICE_IMPLANTS,
      '/profile/health-profile/birth-complications': MedicalDetailType.BIRTH_COMPLICATIONS,
      '/profile/health-profile/vaccination-tracker': MedicalDetailType.VACCINE_TRACKER,
      '/profile/health-profile/pet-care': MedicalDetailType.PET_CARE,
      '/profile/health-profile/notes': MedicalDetailType.NOTE,
    };
    
    let finalPath = path;
    const moduleType = pathToTypeMap[path];
    
    if (moduleType) {
      // Add query parameter to auto-open the "Do you have?" modal
      finalPath = `/profile/health-profile?open=${moduleType}`;
    }
    
    if (onNavigate) {
      onNavigate(finalPath);
    }
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.card} onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className={styles.closeButton} onClick={onClose}>
          <IconX size={24} color="#2A2A2A" />
        </button>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLabel}>Profile strength</div>
          <div className={styles.headerTitle}>{config.title}</div>
        </div>

        {/* Progress Bars */}
        <div className={styles.progressBars}>
          {[1, 2, 3].map((bar) => (
            <div
              key={bar}
              className={`${styles.progressBar} ${
                bar <= config.filledBars ? styles.progressBarFilled : styles.progressBarEmpty
              }`}
            />
          ))}
        </div>

        {/* Description */}
        <div className={styles.description}>{config.description}</div>

        {/* Actions */}
        {config.actions.length > 0 && (
          <div className={styles.actions}>
            {config.actions.map((action, index) => (
              <MNavigationButton
                key={index}
                text={action.text}
                description={action.description}
                rightIcon="chevron-right"
                variant="border"
                onClick={() => handleActionClick(action.path)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileTrackerCard;
